import { MarketOutcome } from '../enums';
import { NormalizedMarket } from '../interfaces';

export const getMarketSides = (market: NormalizedMarket): [string, string] => {
	const { name, selection } = market;
	const parts = name.split(/::|--/).map((p) => p.trim());

	const otherSides = parts.filter((p) => p !== selection);

	const sideA = selection;
	const sideB = otherSides[0] || selection;

	return [sideA, sideB];
};

export const getPairPlayers = (m: NormalizedMarket) => {
	const name = m.selection;

	if (name.includes('+')) {
		const parts = name.split('+').map((x) => x.trim());

		return [parts[0], parts[1]];
	} else {
		const parts = name.split(' ');

		if (parts.length >= 2) {
			const playerB = parts.slice(-2).join(' ');
			const playerA = parts.slice(0, -2).join(' ');

			return [playerA, playerB];
		}

		return [name, ''];
	}
};

export const extractNumber = (text: string): number => {
	const matches = text.match(/\b\d+(\.\d+)?\b/g);
	if (!matches) return 0;

	return Number(matches[matches.length - 1]);
};

export const roundThresholdUp = (points: number): number => Math.ceil(points);

// OpticOdds component touchdown stats. `misc_touchdowns` is unreliable — it dropped
// Mahomes's rushing TD in KC/DEN 2026-09-14, undercounting the game 4 vs 5 — so every
// TD market sums these components instead. `passing_touchdowns` is intentionally excluded:
// a passing TD is the same scoring play as the receiver's receiving TD, so counting both
// double-counts, and for "did this player score" the QB's passing TD is not the QB scoring.
export const TOUCHDOWN_COMPONENT_KEYS = [
	'rushing_touchdowns',
	'receiving_touchdowns',
	'defensive_touchdowns',
	'kick_return_touchdowns',
	'punt_return_touchdowns',
	'interception_touchdowns',
];

/** Virtual stat key: pass to any stat reader below to get real total touchdowns. */
export const TOTAL_TOUCHDOWNS = 'total_touchdowns';

/**
 * Reads a stat from a flat stats object. Behaviour-preserving for every real key; for the
 * TOTAL_TOUCHDOWNS sentinel it returns the summed TD components instead of a single field.
 */
export const readStat = (stats: any, statKey: string): number =>
	statKey === TOTAL_TOUCHDOWNS
		? TOUCHDOWN_COMPONENT_KEYS.reduce((sum, k) => sum + (Number(stats?.[k]) || 0), 0)
		: (stats?.[statKey] ?? 0);

export const getPlayerResult = (playerName: string, r: any) =>
	r?.player_results?.find((p: any) => p.player?.name === playerName)?.market_stats;

export const getPlayerStats = (playerName: string, r: any) =>
	r?.player_results?.find((p: any) => p.player?.name === playerName)?.stats?.[0]?.stats ?? {};

export const getPlayerPeriodStat = (
	playerName: string,
	r: any,
	period: string,
	statKey: string
): number => {
	const periodStats = r?.player_results
		?.find((p: any) => p.player?.name === playerName)
		?.stats?.find((s: any) => s.period === period)?.stats;
	return readStat(periodStats, statKey);
};

export const sumPlayerPeriodsStat = (
	playerName: string,
	r: any,
	periods: string[],
	statKey: string
): number =>
	periods.reduce((sum, period) => sum + getPlayerPeriodStat(playerName, r, period, statKey), 0);

export const getTeamStat = (r: any, isHome: boolean, statKey: string): number =>
	(isHome ? r?.score?.stats?.home : r?.score?.stats?.away)?.[0]?.stats?.[statKey] ?? 0;

/** Sums a per-period player stat across every player on a team (roster looked up via player_results[i].team.name). */
export const sumTeamPeriodStat = (
	r: any,
	teamName: string,
	period: string,
	statKey: string
): number =>
	r?.player_results
		?.filter((p: any) => p.team?.name === teamName)
		?.reduce(
			(sum: number, p: any) =>
				sum + readStat(p.stats?.find((s: any) => s.period === period)?.stats, statKey),
			0
		) ?? 0;

export const sumTeamPeriodsStat = (
	r: any,
	teamName: string,
	periods: string[],
	statKey: string
): number =>
	periods.reduce((sum, period) => sum + sumTeamPeriodStat(r, teamName, period, statKey), 0);

export const resolveCondition = (condition: boolean, isCompleted: boolean): MarketOutcome => {
	if (isCompleted) {
		return condition ? MarketOutcome.Won : MarketOutcome.Lost;
	}

	return condition ? MarketOutcome.Won : MarketOutcome.Pending;
};

/** Like resolveCondition, but refunds undetermined markets when the match ended early. */
export const resolveConditionAtEnd = (
	condition: boolean,
	isCompleted: boolean,
	endedEarly = false
): MarketOutcome => {
	if (condition) return MarketOutcome.Won;
	// endedEarly (e.g. a cancelled fixture) can be true while isCompleted is still false —
	// OO does not mark a cancelled fixture completed — so it must outrank the completed gate.
	if (endedEarly) return MarketOutcome.Void;
	if (!isCompleted) return MarketOutcome.Pending;
	return MarketOutcome.Lost;
};

export const extractSelection = (question: string, prefix: string, suffixes: string[]): string => {
	const suffixPattern = suffixes.map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
	const regex = new RegExp(`${prefix}(.+?)\\s(?:${suffixPattern})`, 'i');
	const match = question.match(regex);

	return match ? match[1].trim() : '';
};

export const extractPlayers = (q: string): [string, string] => {
	const regex = /Will (.+?) and (.+?) combine/i;
	const match = q.match(regex);

	if (match) {
		return [match[1].trim(), match[2].trim()];
	}

	return ['', ''];
};
